import React, { useState, useEffect } from 'react';
import { getPackages, purchasePackage } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './PackagesModal.css';

function PackagesModal({ isOpen, onClose, onPurchaseSuccess }) {
    const { user } = useAuth();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedPackage, setSelectedPackage] = useState(null);

    useEffect(() => {
        if (isOpen) {
            loadPackages();
        }
    }, [isOpen]);

    const loadPackages = async () => {
        try {
            const data = await getPackages();
            setPackages(data.filter(pkg => pkg.isActive));
        } catch (error) {
            console.error('Error loading packages:', error);
            setError('Failed to load packages');
        }
    };

    useEffect(() => {
        // Setup purchase success/error callbacks for WebView
        window.onPurchaseSuccess = async (purchaseToken, orderId, productId) => {
            console.log('Purchase success from native:', { purchaseToken, orderId, productId });
            
            if (!selectedPackage) {
                console.error('No package selected');
                setError('Purchase completed but package not found');
                setLoading(false);
                return;
            }

            try {
                // Send to backend for verification
                const response = await purchasePackage(user.id, selectedPackage._id, 'googleplay', null, {
                    purchaseToken,
                    productId,
                    orderId,
                    packageName: 'com.nextchat.meetzone'
                });
                
                if (response.success) {
                    onPurchaseSuccess(response.balance);
                    onClose();
                } else {
                    setError(response.message || 'Purchase verification failed');
                }
            } catch (error) {
                console.error('Backend verification error:', error);
                setError(error.response?.data?.message || 'Failed to verify purchase');
            } finally {
                setLoading(false);
            }
        };

        window.onPurchaseError = (error) => {
            console.error('Purchase error from native:', error);
            setError('Purchase failed: ' + error);
            setLoading(false);
        };

        // Load Google Pay API script (only for web, not WebView)
        if (!isWebView() && !window.google?.payments) {
            const script = document.createElement('script');
            script.src = 'https://pay.google.com/gp/p/js/pay.js';
            script.async = true;
            script.onload = () => {
                console.log('Google Pay API loaded');
            };
            script.onerror = () => {
                console.error('Failed to load Google Pay API');
            };
            document.body.appendChild(script);
        }

        // Cleanup
        return () => {
            delete window.onPurchaseSuccess;
            delete window.onPurchaseError;
        };
    }, [selectedPackage, user.id, onPurchaseSuccess, onClose]);

    // Check if running in WebView
    const isWebView = () => {
        return !!(window.Android || (window.webkit && window.webkit.messageHandlers));
    };

    // WebView purchase handler (Google Play Billing)
    const handleWebViewPurchase = async (pkg) => {
        if (!pkg.productId) {
            setError('Product ID not found for this package');
            return;
        }

        setSelectedPackage(pkg);
        setLoading(true);
        setError('');

        try {
            // Check if Android bridge is available
            if (window.Android && window.Android.purchasePackage) {
                console.log('Starting purchase via Android bridge:', pkg.productId);
                window.Android.purchasePackage(pkg.productId);
            } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.purchase) {
                // iOS WebView (if needed)
                window.webkit.messageHandlers.purchase.postMessage({
                    productId: pkg.productId
                });
            } else {
                setError('WebView bridge not available. Please use the Android app.');
                setLoading(false);
            }
        } catch (error) {
            console.error('WebView purchase error:', error);
            setError('Failed to start purchase. Please try again.');
            setLoading(false);
        }
    };

    const handleGooglePay = async (pkg) => {
        // Check if in WebView - use native purchase
        if (isWebView()) {
            handleWebViewPurchase(pkg);
            return;
        }

        // Normal web - use Google Pay (web)
        setSelectedPackage(pkg);
        setLoading(true);
        setError('');

        try {
            // Wait for Google Pay API to load
            if (!window.google || !window.google.payments) {
                // Wait a bit and try again
                setTimeout(() => {
                    if (window.google && window.google.payments) {
                        initializeGooglePay(pkg);
                    } else {
                        setError('Google Pay is not available. Please refresh the page.');
                        setLoading(false);
                    }
                }, 1000);
            } else {
                initializeGooglePay(pkg);
            }
        } catch (error) {
            console.error('Google Pay error:', error);
            setError('Payment failed. Please try again.');
            setLoading(false);
        }
    };

    const initializeGooglePay = (pkg) => {
        const paymentsClient = new window.google.payments.api.PaymentsClient({
            environment: process.env.REACT_APP_GOOGLE_PAY_ENV || 'TEST' // TEST or PRODUCTION
        });

        const paymentDataRequest = {
            apiVersion: 2,
            apiVersionMinor: 0,
            merchantInfo: {
                merchantId: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID || 'BCR2DN6TZ6P6VX4Q',
                merchantName: 'Next'
            },
            allowedPaymentMethods: [{
                type: 'CARD',
                parameters: {
                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                    allowedCardNetworks: ['AMEX', 'DISCOVER', 'JCB', 'MASTERCARD', 'VISA']
                },
                tokenizationSpecification: {
                    type: 'PAYMENT_GATEWAY',
                    parameters: {
                        gateway: 'example',
                        gatewayMerchantId: 'exampleGatewayMerchantId'
                    }
                }
            }],
            transactionInfo: {
                totalPriceStatus: 'FINAL',
                totalPriceLabel: 'Total',
                totalPrice: pkg.price.toFixed(2),
                currencyCode: 'USD',
                countryCode: 'US'
            },
            callbackIntents: ['PAYMENT_AUTHORIZATION']
        };

        paymentsClient.loadPaymentData(paymentDataRequest)
            .then(paymentData => {
                // Process payment
                processPayment(pkg, paymentData);
            })
            .catch(error => {
                console.error('Google Pay error:', error);
                if (error.statusCode !== 'CANCELED') {
                    setError('Payment failed. Please try again.');
                }
                setLoading(false);
            });
    };

    const processPayment = async (pkg, paymentData) => {
        try {
            // Send payment data to backend for verification
            const response = await purchasePackage(user.id, pkg._id, 'googlepay', paymentData);
            
            if (response.success) {
                onPurchaseSuccess(response.balance);
                onClose();
            } else {
                setError(response.message || 'Payment failed');
            }
        } catch (error) {
            console.error('Payment processing error:', error);
            setError(error.response?.data?.message || 'Payment failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="packages-modal-overlay" onClick={onClose}>
            <div className="packages-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Buy Minutes</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                
                <div className="modal-content">
                    {error && (
                        <div className="error-message">{error}</div>
                    )}

                    {packages.length === 0 ? (
                        <div className="no-packages">
                            <p>No packages available at the moment.</p>
                        </div>
                    ) : (
                        <div className="packages-grid">
                            {packages.map(pkg => (
                                <div key={pkg._id} className="package-card">
                                    <div className="package-name">{pkg.name}</div>
                                    <div className="package-minutes">{pkg.minutes} minutes</div>
                                    <div className="package-price">${pkg.price.toFixed(2)}</div>
                                    <button
                                        className={isWebView() ? "btn-purchase" : "btn-google-pay"}
                                        onClick={() => handleGooglePay(pkg)}
                                        disabled={loading}
                                    >
                                        {loading && selectedPackage?._id === pkg._id ? (
                                            'Processing...'
                                        ) : isWebView() ? (
                                            <>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                                </svg>
                                                Buy Now
                                            </>
                                        ) : (
                                            <>
                                                <svg width="20" height="20" viewBox="0 0 24 24">
                                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                                </svg>
                                                Pay with Google Pay
                                            </>
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PackagesModal;

